import React, {useEffect, useState} from 'react';
import {Formik} from "formik";
import s from "./CardTransactions.module.css";
import {ConfigProvider, DatePicker} from "antd";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import Table, {ColumnsType} from "antd/es/table";
import {getCardTransactionList} from "../../../store/reducers/ActionCreators";
import {useAppDispatch, useAppSelector} from "../../../hooks/redux";
import {setDisabled} from "../../../store/reducers/UserSlice";
import {Loader} from "../../Loader/Loader";
import CustomDropdown from '../../ui/CustomDropdown/CustomDropdown';

dayjs.extend(weekday)
dayjs.extend(localeData)

const dateFormat = 'MMMM YYYY';

const CardTransactions = () => {
    const [isMobile, setIsMobile] = useState(false)
    
    const currentDate = new Date();
    const safeInitialDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    
    const [date, setDate] = useState({
        from: safeInitialDate,
    });

    const getValidDayjsDate = (dateString: string) => {
        const dateObj = dayjs(dateString);
        return dateObj.isValid() ? dateObj : dayjs();
    };

    const formattedDate = getValidDayjsDate(date.from).format('MM-YYYY');
    const [month, year] = formattedDate.split("-");

    const dateStyle = {
        fontFamily: 'Inter',
        fontWeight: 600,
        fontSize: 14,
        lineHeight: '18px',
        color: '#2A2E37',
        padding: '17px 16px',
        height: 52,
        width: isMobile ? "46%" : 220,
        border: '1.5px solid #EEEFEF',
        borderRadius: 8
    }

    const dispatch = useAppDispatch()
    const {userToken} = useAppSelector(store => store.userReducer.user)
    const transactions = useAppSelector(store => store.cardTransactionsReducer.cardTransactionsData)
    const {isLoading} = useAppSelector(store => store.userReducer)
    const userCards: any[any] = useAppSelector(store => store.userReducer.cardInfo)
    const [selectedCard, setSelectedCard] = useState('');
    const cardNumbers: any[''] = userCards.map((card: any) => card.cardNumber);

    const [providerType, setProviderType] = useState<'intercash' | 'stradacarte' | null>(null);

    useEffect(() => {
        if (cardNumbers.length === 1 && !selectedCard) {
            setSelectedCard(cardNumbers[0]);
        }
    }, [cardNumbers, selectedCard]);

    useEffect(() => {
        if (window.innerWidth <= 768) {
            setIsMobile(true)
        } else {
            setIsMobile(false)
        }
    }, [])

    const cardRemoteIdLookup = userCards.reduce((lookup: any, card: any) => {
        lookup[card.cardNumber] = card.remoteId;
        return lookup;
    }, {});

    const getRemoteIdByCardNumber = (cardNumber: any) => cardRemoteIdLookup[cardNumber];

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (selectedCard && getRemoteIdByCardNumber(selectedCard)) {
            dispatch(getCardTransactionList(userToken, getRemoteIdByCardNumber(selectedCard), year, month))
        }
    }, [year, month, dispatch, userToken, selectedCard])
    /* eslint-enable react-hooks/exhaustive-deps */

    useEffect(() => {
        if (!transactions || !transactions.length) {
            setProviderType(null);
            return;
        }

        const firstItem = transactions[0];
        if (firstItem.transType !== undefined || firstItem.merchantName !== undefined) {
            setProviderType('stradacarte');
        } else {
            setProviderType('intercash');
        }
    }, [transactions]);

    const transactionsArray = Array.isArray(transactions) ? transactions : [];
    
    const getColumns = (): ColumnsType<any> => {
        if (providerType === 'stradacarte') {
            return [
                {
                    title: 'Date',
                    dataIndex: 'dateDisplay',
                    render: (text, record) => {
                        const dateCreated = record.dateCreated || 'N/A';
                        return dateCreated.split('T')[0] || 'N/A';
                    }
                },
                {
                    title: 'Description',
                    dataIndex: 'description',
                },
                {
                    title: 'Amount',
                    dataIndex: 'amount',
                },
                {
                    title: 'Merchant',
                    dataIndex: 'merchantName',
                },
                {
                    title: 'Reference',
                    dataIndex: 'authRefNum',
                    ellipsis: true,
                }
            ];
        }
        
        // Default/Intercash columns
        return [
            {
                title: 'Date',
                dataIndex: 'date',
            },
            {
                title: 'Description',
                dataIndex: 'description',
            },
            {
                title: 'Amount',
                dataIndex: 'amount',
                align: 'right'
            },
            {
                title: 'Currency',
                dataIndex: 'currency',
                align: 'right'
            }
        ];
    };

    const tableData = transactionsArray.map((item: any, index) => {
        if (providerType === 'stradacarte') {
            const currencyCode = item.merchantCurrency === '840' ? 'USD' : 
                               item.merchantCurrency === '978' ? 'EUR' : 
                               item.merchantCurrency || 'N/A';
            
            return {
                key: item.authRefNum || index,
                dateCreated: item.dateCreated || 'N/A',
                dateSettled: item.dateSettled || 'N/A',
                description: item.description || 'N/A',
                amount: `${item.amount || 0} ${currencyCode}`,
                merchantName: item.merchantName || 'N/A',
                merchantAmount: item.merchantAmount || 'N/A',
                authRefNum: item.authRefNum || 'N/A',
                transType: item.transType,
                transStatus: item.transStatus
            };
        }
        
        return {
            key: item.Date || item.date || index,
            date: item.Date || item.date || 'N/A',
            amount: item.Amount || item.amount || 0,
            description: item.Desciption || item.description || 'N/A',
            currency: item.Currency || item.currency || 'N/A'
        };
    });

    const renderMobileTransactionItem = (item: any, index: number) => {
        if (providerType === 'stradacarte') {
            const currencyCode = item.merchantCurrency === '840' ? 'USD' : 
                               item.merchantCurrency === '978' ? 'EUR' : 
                               item.merchantCurrency || 'N/A';
            
            const date = item.dateCreated ? item.dateCreated.split('T')[0] : 'N/A';
            
            return (
                <div key={item.authRefNum || index} className={s.transfer}>
                    <div className={s.mobileTransferRow}>
                        <div className={s.mobileTransferBlock}>
                            <div className={s.blockTitle}><React.Fragment>Date</React.Fragment></div>
                            <p className={s.dateCellP}>{date}</p>
                        </div>
                    </div>

                    <div className={s.mobileTransferRow}>
                        <div className={s.mobileTransferBlock}>
                            <div className={s.blockTitle}><React.Fragment>Description</React.Fragment></div>
                            <p className={s.dateCellP}>{item.description || 'N/A'}</p>
                        </div>
                    </div>

                    <div className={s.mobileTransferRow}>
                        <div className={s.mobileTransferBlock}>
                            <div className={s.blockTitle}><React.Fragment>Amount</React.Fragment></div>
                            <p>{item.amount || '0'} {currencyCode}</p>
                        </div>
                    </div>

                    <div className={s.mobileTransferRow}>
                        <div className={s.mobileTransferBlock}>
                            <div className={s.blockTitle}><React.Fragment>Merchant</React.Fragment></div>
                            <p className={s.dateCellP}>{item.merchantName || 'N/A'}</p>
                        </div>
                    </div>
                </div>
            );
        }
        
        return (
            <div key={item.Date || item.date || index} className={s.transfer}>
                <div className={s.mobileTransferRow}>
                    <div className={s.mobileTransferBlock}>
                        <div className={s.blockTitle}><React.Fragment>Date</React.Fragment></div>
                        <p className={s.dateCellP}>{item.Date || item.date || 'N/A'}</p>
                    </div>
                </div>

                <div className={s.mobileTransferRow}>
                    <div className={s.mobileTransferBlock}>
                        <div className={s.blockTitle}><React.Fragment>Description</React.Fragment></div>
                        <p className={s.dateCellP}>{item.Desciption || item.description || 'N/A'}</p>
                    </div>
                </div>

                <div className={s.mobileTransferRow}>
                    <div className={s.mobileTransferBlock}>
                        <div className={s.blockTitle}><React.Fragment>Amount</React.Fragment></div>
                        <p>
                            {(item.Amount || item.amount || 0) + ' ' + (item.Currency || item.currency || 'N/A')}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    return (<>
        <ConfigProvider
            theme={{
                components: {
                    DatePicker: {
                        colorPrimary: 'var(--primary-color)', fontWeightStrong: 600, colorLink: 'red'
                    }
                }, token: {
                    colorPrimary: 'var(--primary-color)', colorBorder: '#EEEFEF', fontFamily: 'Inter',
                },
            }}
        >
            <Formik
                initialValues={{
                    from: '',
                    card: cardNumbers.length === 1 ? cardNumbers[0] : selectedCard
                }}
                onSubmit={(values) => {
                    if (values.card && getRemoteIdByCardNumber(values.card)) {
                        dispatch(setDisabled(true))
                        dispatch(getCardTransactionList(userToken, getRemoteIdByCardNumber(values.card), year, month))
                            .then((res) => {
                                console.log('success', res)
                            })
                            .catch((e) => {
                                console.log(e)
                            })
                            .finally(() => {
                                dispatch(setDisabled(false))
                            })
                    }
                }}
            >
                {({
                      values, handleChange, handleBlur, handleSubmit
                  }) => (<form className={s.form} onSubmit={handleSubmit}>
                    <div className={s.periodRowContainer}>
                        <div className={s.periodRow}>
                            <div className={s.dropdown_wrapper}>
                                <p className={s.labelText}>Period</p>
                                <DatePicker
                                    suffixIcon={null}
                                    style={dateStyle}
                                    picker="month"
                                    value={getValidDayjsDate(date.from)}
                                    format={dateFormat}
                                    onChange={(dayjsDate, dateString) => {
                                        if (!dayjsDate || !dateString || !dayjsDate.isValid()) {
                                            setDate({ ...date, from: safeInitialDate });
                                        } else {
                                            const year = dayjsDate.year();
                                            const month = dayjsDate.month() + 1;
                                            const formattedDate = `${year}-${String(month).padStart(2, '0')}-01`;
                                            setDate({ ...date, from: formattedDate });
                                        }
                                    }}
                                    allowClear={false}
                                    disabledDate={(current) => {
                                        return current && current > dayjs().endOf('day');
                                    }}
                                />
                            </div>
                        </div>

                        <div className={s.periodRow}>
                            <div className={s.dropdown_wrapper}>
                                <p className={s.labelText}>Card</p>
                                <CustomDropdown
                                    items={cardNumbers}
                                    id={"card"}
                                    name={"card"}
                                    disable={cardNumbers.length === 1}
                                    placeholder={cardNumbers.length === 1 ? cardNumbers[0] : 'Select a card...'}
                                    onChange={(val) => val && setSelectedCard(val)}
                                />
                            </div>
                        </div>
                    </div>
                </form>)}
            </Formik>
        </ConfigProvider>

        <div className={s.tableWrapper}>
            {isLoading ? <Loader/> : (
                !selectedCard ? (
                    <div className={s.emptyState}>
                        <div className={s.emptyIcon}>
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#979797" strokeWidth="1">
                                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                <line x1="1" y1="10" x2="23" y2="10"></line>
                                <circle cx="6" cy="15" r="1" fill="#979797"></circle>
                                <circle cx="18" cy="15" r="1" fill="#979797"></circle>
                                <path d="M9 15h6" stroke="#979797" strokeWidth="1"></path>
                            </svg>
                        </div>
                        <h3 className={s.emptyTitle}>Select a Card to View Transactions</h3>
                        <div className={s.emptyDescription}>
                            <p>
                                Please select a card from the dropdown above to view your transaction history.
                            </p>
                            <p className={s.supportText}>
                                You can view transactions for each card individually by selecting it from the card dropdown menu.
                            </p>
                        </div>
                    </div>
                ) : (!transactionsArray || transactionsArray.length === 0 ? (
                    <div className={s.emptyState}>
                        <div className={s.emptyIcon}>
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#979797" strokeWidth="1">
                                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                <line x1="1" y1="10" x2="23" y2="10"></line>
                                <circle cx="6" cy="15" r="1" fill="#979797"></circle>
                                <circle cx="18" cy="15" r="1" fill="#979797"></circle>
                                <path d="M9 15h6" stroke="#979797" strokeWidth="1"></path>
                            </svg>
                        </div>
                        <h3 className={s.emptyTitle}>No Card Transactions Found</h3>
                        <div className={s.emptyDescription}>
                            <p>
                                We couldn't find any transactions for the selected card and period. 
                                This could be due to:
                            </p>
                            <ul className={s.reasonsList}>
                                <li>No transactions occurred during this period</li>
                                <li>Transaction history may not be available for this card type</li>
                                <li>Your account may need additional verification for transaction access</li>
                            </ul>
                            <p className={s.supportText}>
                                If you believe you should have transaction records or need assistance, 
                                please <strong>contact our support team</strong> for further help.
                            </p>
                        </div>
                        <div className={s.emptyActions}>
                            <button 
                                className={s.refreshButton}
                                onClick={() => {
                                    if (selectedCard && getRemoteIdByCardNumber(selectedCard)) {
                                        dispatch(getCardTransactionList(userToken, getRemoteIdByCardNumber(selectedCard), year, month));
                                    }
                                }}
                            >
                                Refresh
                            </button>
                        </div>
                    </div>
                ) : (
                    isMobile ? (
                        <div className={s.transactionsListMobile}>
                            {transactionsArray.map((item, index) => renderMobileTransactionItem(item, index))}
                        </div>
                    ) : (
                        <ConfigProvider theme={{
                            components: {
                                Pagination: {
                                    colorPrimary: '#FFFFFF',
                                    colorPrimaryHover: '#FFFFFF',
                                    colorBgContainer: 'var(--primary-color)',
                                    colorLink: '#FFFFFF'
                                },
                            },
                        }}>
                            <Table
                                bordered={false}
                                style={{marginTop: 48}}
                                rowClassName={(record, index) => index % 2 !== 0 ? `${s.table_row_dark}` : ''}
                                className={s.statementTable}
                                columns={getColumns()}
                                pagination={{position: ['bottomCenter'], defaultPageSize: 15}}
                                dataSource={tableData}
                            />
                        </ConfigProvider>
                    )
                ))
            )}
        </div>
    </>);
};

export default CardTransactions;